import { FC, useEffect, useMemo, useState } from "react";
import { HeaderComponent } from "../../components";
import HeartRateImage from "../../assets/images/onboarding/heartrate.png";
import {
    IonButton,
    IonCard,
    IonContent,
    IonFooter,
    IonItem,
    IonLabel,
    IonLoading,
    IonPage,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useMatch } from "../../components/hooks/match";
import { HrData, MatchPhone } from "refsix-js-models";
import {
    FitnessDataProvider,
    useFitnessDataContext,
} from "../../components/hooks";
import { trackEvent } from "../../services/analytics/analyticsService";
import { routes } from "../../route/constants";
import "../onBoarding/onBoardingShareHealthData.css";
import { isIos } from "../../services/platformDetection";
import HeartRateGraphComponent from "../../components/HeartRateGraphComponent";
import { ScorzaCard } from "../../components/stats/ScorzaCard";
import exampleMatch from "../../support/example-result.json";

const demoMatchId = "example-result1";
const fallbackMatch = exampleMatch.match as unknown as MatchPhone;
const fallbackHrData = exampleMatch.hr as HrData;

export const PermissionsHealthDataWrapper: React.FC = () => {
    return (
        <FitnessDataProvider matchId={demoMatchId}>
            <PermissionsHealthData />
        </FitnessDataProvider>
    );
};

export const PermissionsHealthData: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const demoMatch = useMatch(demoMatchId);
    const matchForDisplay = useMemo<MatchPhone>(
        () => demoMatch ?? fallbackMatch,
        [demoMatch]
    );
    const [filteredHrData, setFilteredHrData] =
        useState<HrData>(fallbackHrData);
    const { hrData, loading } = useFitnessDataContext();

    useEffect(() => {
        if (
            demoMatch?.matchFinished &&
            hrData?.heartRateValues &&
            hrData.heartRateValues.length > 0
        ) {
            setFilteredHrData(hrData);
        }
    }, [hrData, demoMatch]);

    const hasHeartRateData =
        filteredHrData?.heartRateValues &&
        filteredHrData.heartRateValues.length > 0;

    const directToMorePage = () => {
        // Navigate back to MORE page
        route.push(routes.tabMore, "back");
    };

    const askForPermission = async () => {
        if (isIos() && window.iospermissions) {
            setIsLoading(true);
            return new Promise((resolve, reject) => {
                window.iospermissions?.requestWorkoutPermissions(
                    (permissions: any) => {
                        setIsLoading(false);
                        trackEvent("shareHealthData", { from: "permissions" });
                        directToMorePage();
                        resolve(permissions);
                    },
                    (error: any) => {
                        setIsLoading(false);
                        directToMorePage();
                        reject(error);
                    }
                );
            });
        }
        setIsLoading(false);
        directToMorePage();
    };

    return (
        <IonPage>
            <HeaderComponent
                showBackButton={true}
                title={t("permissions.PermissionsDirective.title")}
            />
            <IonContent>
                <IonLoading isOpen={isLoading} />
                <IonItem lines="none" color="transparent">
                    <IonLabel
                        className="align-center text-weight-bold"
                        color="primary"
                    >
                        {t("help.onBoarding.shareHealthData")}
                    </IonLabel>
                </IonItem>
                <IonItem lines="none" color="transparent">
                    <IonText className="align-center">
                        {t("help.onBoarding.trackFitness")}
                    </IonText>
                </IonItem>

                {hasHeartRateData && (
                    <ScorzaCard title={"Heart Rate Zones"}>
                        <HeartRateGraphComponent
                            match={matchForDisplay}
                            filteredHrData={filteredHrData}
                        />
                    </ScorzaCard>
                )}

                {!hasHeartRateData && !loading && (
                    <IonCard
                        color="transparent"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <IonItem lines="none" color="transparent">
                            <img src={HeartRateImage} alt="HearRate Image" />
                        </IonItem>
                    </IonCard>
                )}
            </IonContent>
            <IonFooter>
                <IonButton
                    expand="block"
                    data-testid={"permissions-health-data-next-button"}
                    onClick={() => askForPermission()}
                >
                    {t("general.next")}
                </IonButton>
            </IonFooter>
        </IonPage>
    );
};
